import { Badge } from "@/modules/shadcn/ui/badge";
import { cn } from "@/lib/utils";
import { Model, ModelStatusEnum } from "@repo/schema";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { TRAINING_TYPE_LABELS } from "../../constants/labels";
import {
  getHeadlineLabel,
  getHeadlineValue,
} from "../../utils/headlineMetric";
import { formatDate, tKey, type I18nKey } from "@/core/i18n";

const formatMetric = (value: number | null | undefined): string => {
  if (value == null || !Number.isFinite(value)) return "-";
  return value.toFixed(3);
};

export interface ModelCardProps {
  model: Model;
  order: number;
}

const STATUS_STYLES: Record<
  ModelStatusEnum,
  { label: I18nKey<"model">; className: string }
> = {
  [ModelStatusEnum.DONE]: {
    label: tKey("model:status.DONE"),
    className: "bg-emerald-100 text-emerald-700",
  },
  [ModelStatusEnum.TRAINING]: {
    label: tKey("model:status.TRAINING"),
    className: "bg-pear-100 text-emerald-700",
  },
  [ModelStatusEnum.CONVERTING]: {
    label: tKey("model:status.CONVERTING"),
    className: "bg-pear-100 text-emerald-700",
  },
  [ModelStatusEnum.DRAFT]: {
    label: tKey("model:status.DRAFT"),
    className: "bg-black/5 text-black/60",
  },
  [ModelStatusEnum.ERROR]: {
    label: tKey("model:status.ERROR"),
    className: "bg-red-100 text-red-600",
  },
  [ModelStatusEnum.CANCELLED]: {
    label: tKey("model:status.CANCELLED"),
    className: "bg-amber-100 text-amber-700",
  },
};

export const ModelCard = ({ model, order }: ModelCardProps) => {
  const { t } = useTranslation(["model", "common"]);
  const { projectId, modelId } = useParams();
  const status = STATUS_STYLES[model.status];
  const isSelected = model.id.toString() === modelId;

  const headlineValue = getHeadlineValue(model);
  const headlineLabel = getHeadlineLabel(model.trainingType, t);

  return (
    <Link
      to={`/projects/${projectId}/models/${model.id}`}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <div
        className={cn(
          "flex cursor-pointer flex-col gap-2 rounded-lg border border-black/10 bg-white p-3 transition-colors hover:border-black/20 hover:bg-black/[0.02]",
          isSelected &&
            "border-emerald-700 bg-emerald-50 hover:border-emerald-700 hover:bg-emerald-50",
        )}
      >
        <div className="flex items-center gap-1">
          <Badge className="h-auto min-w-6 rounded-full bg-black/5 px-1.5 py-0.5 text-[11px] font-normal text-black/60">
            {t("card.version", { order })}
          </Badge>
          <Badge
            className={cn(
              "h-auto rounded-full px-1.5 py-0.5 text-[11px] font-normal",
              status.className,
            )}
          >
            {t(status.label)}
          </Badge>
        </div>
        <p className="text-base font-bold leading-6 text-black/90">
          {model.name}
        </p>
        <p className="text-xs leading-4 text-black/60">
          {formatDate(model.createdAt)}
        </p>
        {model.status === ModelStatusEnum.DONE && (
          <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-2 border-t border-black/5 pt-2">
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase leading-3 tracking-[0.5px] text-black/50">
                {t("card.type")}
              </span>
              <span className="text-xs font-semibold leading-4 text-black/90">
                {t(TRAINING_TYPE_LABELS[model.trainingType])}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[10px] uppercase leading-3 tracking-[0.5px] text-black/50">
                {t("fields.epochs")}
              </span>
              <span className="text-xs font-semibold leading-4 text-black/90">
                {model.epochs}
              </span>
            </div>
            {(headlineValue != null || model.finalLoss != null) && (
              <>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase leading-3 tracking-[0.5px] text-black/50">
                    {headlineLabel}
                  </span>
                  <span className="text-xs font-semibold leading-4 text-emerald-700">
                    {formatMetric(headlineValue)}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase leading-3 tracking-[0.5px] text-black/50">
                    {t("metrics.loss")}
                  </span>
                  <span className="text-xs font-semibold leading-4 text-black/90">
                    {formatMetric(model.finalLoss)}
                  </span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};
